import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2,
  ArrowRight,
  ShieldCheck,
  ExternalLink,
  RefreshCw,
  Landmark,
  Clock,
  Check,
  Lock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import OnboardingShell from '../components/OnboardingShell';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Step 3 of restaurant onboarding — Stripe Connect.
 *
 * Hits the existing /api/billing/stripe-connect/onboard endpoint which:
 *   1. Creates a Stripe Connect account if missing (real Stripe.Account.create).
 *   2. Generates a dynamic onboarding URL (real account_link).
 *   3. Returns the URL.
 * We redirect the owner to Stripe's official onboarding. Stripe sends them
 * back to /onboarding/stripe-return.
 */
const OnboardingStripeConnectPage = () => {
  const navigate = useNavigate();
  const { token, restaurant, fetchMe } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [statusChecking, setStatusChecking] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  // 'existing' | 'new'  — purely UX (the underlying Stripe onboarding URL is
  // the same for both; Stripe auto-detects an existing account by email).
  const [accountChoice, setAccountChoice] = useState(null);

  useEffect(() => {
    if (!token) {
      // iter123 — Do NOT redirect anonymous visitors away. They are
      // allowed to PREVIEW Step 3 (a la iter115's locked-preview pattern
      // already shipped on Step 4). The render path below detects the
      // !token state and overlays a click-shield + amber banner that
      // points them back to Step 1. Skipping the status fetch here
      // avoids a guaranteed 401 in the console.
      return;
    }
    // Read current status so we can show "already connected → continue" if applicable.
    (async () => {
      try {
        const rid = restaurant?.id;
        if (!rid) {
          // Try to hydrate via /onboarding/state
          const s = await axios.get(`${API}/api/onboarding/state`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setStripeStatus({ already: s.data?.checklist?.stripe_connected });
          return;
        }
        const r = await axios.get(`${API}/api/restaurants/${rid}/stripe/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStripeStatus(r.data);
      } catch {
        setStripeStatus({});
      }
    })();
  }, [token, restaurant?.id, navigate]);

  // iter123 — When the visitor has no auth token, render this page in a
  // "locked preview" mode: they can SEE the Stripe Connect content but
  // every interaction surfaces a toast routing them back to Step 1.
  const isLockedPreview = !token;
  const lockMessage = 'Complete Step 1 (Create your account) first to unlock Stripe Connect.';
  const onLockedClick = () => {
    toast.error(lockMessage, {
      description: 'Your restaurant account is needed before we can route Stripe payouts to you.',
      action: {
        label: 'Go to Step 1',
        onClick: () => navigate('/onboarding/account'),
      },
    });
  };

  const launchOnboarding = async () => {
    if (!restaurant?.id) {
      toast.error('Restaurant context not loaded. Please refresh.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/restaurants/${restaurant.id}/stripe/onboard`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const url = res.data?.onboarding_url;
      if (!url) throw new Error('Stripe did not return an onboarding URL.');
      // Hard-navigate to Stripe — they'll redirect to /onboarding/stripe-return when done.
      window.location.href = url;
    } catch (e) {
      // Surface the real error so the restaurant owner (and our support team)
      // can see what Stripe actually rejected. The previous generic message
      // hid critical info like "Connect is not enabled in live mode" or
      // "Your account does not have permission to create accounts".
      // eslint-disable-next-line no-console
      console.error('[stripe/onboard] failed:', e?.response?.status, e?.response?.data, e?.message);

      const status = e?.response?.status;
      const backendDetail = e?.response?.data?.detail;
      let message;
      if (backendDetail) {
        // Backend already gives us the Stripe error verbatim — show it.
        message = String(backendDetail);
      } else if (!e?.response) {
        // No HTTP response → network/CORS/timeout. Make that obvious.
        message = `Could not reach the NoFee server (${e?.message || 'network error'}). Check your connection and try again.`;
      } else {
        message = `Stripe onboarding failed (HTTP ${status || '?'}). Please try again or contact support.`;
      }
      toast.error(message, { duration: 12000, closeButton: true });
      setSubmitting(false);
    }
  };

  const recheckStatus = async () => {
    if (!restaurant?.id) return;
    setStatusChecking(true);
    try {
      const r = await axios.get(`${API}/api/restaurants/${restaurant.id}/stripe/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStripeStatus(r.data);
      // Accept either `charges_enabled` (fully operational) OR
      // `details_submitted` (user finished Stripe's forms — Stripe may still
      // be asynchronously enabling charges, which is fine for advancing).
      if (r.data?.charges_enabled || r.data?.details_submitted || r.data?.onboarding_complete) {
        await axios.post(
          `${API}/api/onboarding/advance`,
          { step: 'choose_plan' },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        await fetchMe?.();
        navigate('/onboarding/plan');
      } else {
        toast.info('Stripe onboarding still incomplete. Continue setup with Stripe to finish.');
      }
    } catch {
      toast.error('Could not verify status. Try again.');
    } finally {
      setStatusChecking(false);
    }
  };

  const alreadyConnected =
    stripeStatus?.charges_enabled === true ||
    stripeStatus?.details_submitted === true ||
    stripeStatus?.onboarding_complete === true ||
    stripeStatus?.already === true;

  return (
    <OnboardingShell
      currentStep="stripe_connect"
      backFallback="/onboarding/payouts"
      testid="onboarding-stripe-connect-page"
    >
        {/* iter123 — Locked preview banner shown when the visitor has no
            auth token yet. They can read the entire Step 3 explanation
            below, but every interaction surfaces a toast pointing them
            back to Step 1. Mirrors the iter115 pattern from Setup page. */}
        {isLockedPreview && (
          <div
            data-testid="stripe-account-lock-banner"
            className="mb-6 rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/[0.14] to-yellow-500/[0.06] p-4 sm:p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between"
          >
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-400/40 flex items-center justify-center shrink-0">
                <Lock className="w-4 h-4 text-amber-300" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">
                  Stripe locked · Step 1 required
                </p>
                <p className="mt-1 text-[14px] sm:text-[14.5px] text-white font-semibold leading-snug">
                  Create your account to unlock Stripe Connect.
                </p>
                <p className="mt-1 text-[12.5px] text-white/65 leading-relaxed">
                  You can preview this page, but connecting Stripe stays locked until your
                  restaurant account is created.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/onboarding/account')}
              data-testid="stripe-account-lock-cta"
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black text-[13.5px] font-extrabold tracking-tight shadow-[0_12px_30px_-10px_rgba(250,204,21,0.55)] transition-all"
            >
              Go to Step 1 — Account
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* iter123 — Locked-preview wrapper. When !token we soften the
            content and lay a transparent shield on top that catches every
            click and surfaces the toast routing them to Step 1. */}
        <div className={isLockedPreview ? 'relative' : ''}>
          {isLockedPreview && (
            <div
              data-testid="stripe-account-lock-shield"
              role="button"
              tabIndex={0}
              aria-label={lockMessage}
              onClick={onLockedClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onLockedClick();
                }
              }}
              className="absolute inset-0 z-30 cursor-not-allowed rounded-2xl"
            />
          )}
          <fieldset disabled={isLockedPreview} className={isLockedPreview ? 'opacity-60 saturate-75' : ''}>
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08]">
            <Landmark className="w-3.5 h-3.5 text-yellow-300" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
              Stripe Connect · Reimbursements
            </span>
          </div>

          <h1
            data-testid="stripe-connect-headline"
            className="mt-5 font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white"
          >
            Receive reimbursements directly to your{' '}
            <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
              bank account
            </span>
            .
          </h1>
          <p
            data-testid="stripe-connect-description"
            className="mt-4 text-[15px] sm:text-base text-white/65 leading-relaxed max-w-2xl"
          >
            Connect your existing Stripe account or create a new one in just a few minutes. Once
            connected, customer payments go{' '}
            <span className="text-white font-semibold">directly to your own Stripe account</span>. NoFee never holds your money.
          </p>

          {alreadyConnected ? (
            <div
              data-testid="stripe-already-connected"
              className="mt-8 p-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06]"
            >
              <div className="flex items-center gap-2 text-emerald-300 text-[13px] font-bold uppercase tracking-[0.18em] mb-2">
                <ShieldCheck className="w-4 h-4" />
                Stripe connected successfully
              </div>
              <p className="text-[14px] text-white/75 mb-5">
                Your restaurant is ready to receive reimbursement payouts. Let's pick your NoFee plan.
              </p>
              <button
                type="button"
                onClick={recheckStatus}
                disabled={statusChecking}
                data-testid="stripe-continue-to-plan"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[52px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[14px] disabled:opacity-60 transition-all shadow-[0_15px_40px_-15px_rgba(250,204,21,0.55)]"
              >
                Continue to plan selection <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              {/* Account choice — two radio cards */}
              <fieldset className="mt-8" data-testid="stripe-account-choice">
                <legend className="text-[13px] uppercase tracking-[0.18em] font-bold text-white/65 mb-3">
                  How would you like to connect?
                </legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    {
                      key: 'existing',
                      title: 'I already have a Stripe account',
                      desc: 'Sign in to Stripe and link your existing account in seconds.',
                      testid: 'stripe-choice-existing',
                    },
                    {
                      key: 'new',
                      title: 'I need to create a Stripe account',
                      desc: 'Stripe will walk you through setup. Typically about 3 minutes.',
                      testid: 'stripe-choice-new',
                    },
                  ].map((opt) => {
                    const checked = accountChoice === opt.key;
                    return (
                      <label
                        key={opt.key}
                        data-testid={opt.testid}
                        className={`group cursor-pointer p-5 rounded-2xl border transition-all ${
                          checked
                            ? 'border-yellow-500/55 bg-yellow-500/[0.06] shadow-[0_18px_40px_-20px_rgba(250,204,21,0.4)]'
                            : 'border-white/10 bg-white/[0.02] hover:border-yellow-500/30 hover:bg-white/[0.04]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="stripe_account_choice"
                          value={opt.key}
                          checked={checked}
                          onChange={() => {
                            // Skip if user re-clicks the same option to avoid
                            // double-counting in telemetry.
                            if (accountChoice === opt.key) return;
                            setAccountChoice(opt.key);
                            // Fire-and-forget funnel telemetry. We intentionally
                            // don't await or surface errors — this should never
                            // block the user from continuing onboarding.
                            try {
                              axios.post(
                                `${API}/api/telemetry/event`,
                                {
                                  event:
                                    opt.key === 'existing'
                                      ? 'stripe_onboard_choice_existing'
                                      : 'stripe_onboard_choice_new',
                                  restaurant_id: restaurant?.id || null,
                                },
                              ).catch(() => {});
                            } catch {
                              /* ignore */
                            }
                          }}
                          className="sr-only"
                        />
                        <div className="flex items-start gap-3">
                          <span
                            className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                              checked
                                ? 'border-yellow-400 bg-yellow-400'
                                : 'border-white/30 group-hover:border-yellow-500/60'
                            }`}
                          >
                            {checked && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                          </span>
                          <div className="leading-tight">
                            <div
                              className={`text-[14.5px] font-semibold tracking-tight ${
                                checked ? 'text-white' : 'text-white/90'
                              }`}
                            >
                              {opt.title}
                            </div>
                            <p className="mt-1.5 text-[12.5px] text-white/55 leading-relaxed">
                              {opt.desc}
                            </p>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              {/* Primary CTA */}
              <div className="mt-7 flex flex-col gap-4 sm:items-start">
                <button
                  type="button"
                  onClick={launchOnboarding}
                  disabled={submitting}
                  data-testid="stripe-onboarding-btn"
                  className="group inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] tracking-tight disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
                >
                  <ExternalLink className="w-4 h-4" />
                  {submitting ? 'Loading Stripe…' : 'Continue with Stripe'}
                </button>

                {/* Helper text */}
                <ul className="space-y-2 max-w-xl" data-testid="stripe-helper-text">
                  <li className="flex items-start gap-2 text-[13px] text-white/70 leading-relaxed">
                    <ShieldCheck className="mt-0.5 w-4 h-4 text-yellow-400/85 shrink-0" />
                    <span data-testid="stripe-help-existing-account">
                      If you already have a Stripe account, simply sign in when prompted.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-[13px] text-white/70 leading-relaxed">
                    <Building2 className="mt-0.5 w-4 h-4 text-yellow-400/85 shrink-0" />
                    <span data-testid="stripe-help-no-account">
                      If you do not have a Stripe account, Stripe will guide you through creating one during setup.
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-[13px] text-white/70 leading-relaxed">
                    <Clock className="mt-0.5 w-4 h-4 text-yellow-400/85 shrink-0" />
                    <span data-testid="stripe-help-payout-window">
                      Customer payments go{' '}
                      <span className="text-white font-semibold">directly to your own Stripe account</span>. NoFee never holds your money — payouts follow your Stripe payout schedule.
                    </span>
                  </li>
                </ul>
              </div>
            </>
          )}

          {/* Secondary: re-check */}
          {!alreadyConnected && stripeStatus?.account_id ? (
            <div className="mt-6 px-5 py-4 rounded-xl border border-white/8 bg-white/[0.02] flex items-center justify-between gap-3">
              <span className="text-[12.5px] text-white/60">
                Already started? Click to re-check your Stripe status.
              </span>
              <button
                type="button"
                onClick={recheckStatus}
                disabled={statusChecking}
                data-testid="stripe-recheck-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/12 hover:border-yellow-500/40 text-white/85 hover:text-white text-[12px] font-bold transition-all"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${statusChecking ? 'animate-spin' : ''}`} />
                {statusChecking ? 'Checking…' : 'Re-check status'}
              </button>
            </div>
          ) : null}
        </div>
          </fieldset>
        </div>
    </OnboardingShell>
  );
};

export default OnboardingStripeConnectPage;
